import { useEffect, useRef, useState } from "react";
import { GlassCard, Button, StatusPill, useToast } from "@/shared/ui";
import { CATALOG, wordsLabel, type PackageId } from "@/shared/api/catalog";
import {
  useCreateOrder,
  useOrderStatus,
  type CreateOrderInput,
} from "@/shared/api/hooks";
import { useSettings } from "@/shared/api/settings";
import { useI18n } from "@/shared/i18n";
import { ApiError } from "@/shared/api/client";
import type { CreateOrderResponse } from "@/shared/api/schemas";
import { orderTone, orderLabelKey } from "./status";
import { OrderTimeline } from "./OrderTimeline";
import { CryptoPayCard } from "./CryptoPayCard";
import "./billing.css";

// Alipay exists server-side but is hidden until the merchant account is live.
type Channel = "stripe" | "usdc";

/** USDC brand mark: white dollar ring on the official blue (#2775CA) disc. */
function UsdcMark() {
  return (
    <svg className="seg__icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#2775CA" />
      <path
        d="M9 4.6a8 8 0 0 0 0 14.8M15 4.6a8 8 0 0 1 0 14.8"
        stroke="#fff"
        strokeWidth="1.7"
        strokeLinecap="round"
        fill="none"
      />
      <text
        x="12"
        y="16.4"
        textAnchor="middle"
        fontSize="12.5"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fill="#fff"
      >
        $
      </text>
    </svg>
  );
}

/** Stripe brand mark: white "S" on the official blurple (#635BFF) tile. */
function StripeMark() {
  return (
    <svg className="seg__icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill="#635BFF" />
      <path
        d="M11.1 9.6c0-.6.5-.9 1.4-.9 1.2 0 2.8.4 4 1.1V6.2c-1.3-.5-2.7-.8-4-.8-3.2 0-5.4 1.7-5.4 4.5 0 4.4 6 3.7 6 5.6 0 .7-.6 1-1.5 1-1.3 0-3.1-.6-4.5-1.4v3.7c1.5.6 3 .9 4.5.9 3.3 0 5.6-1.6 5.6-4.5 0-4.7-6.1-3.9-6.1-5.6z"
        fill="#fff"
      />
    </svg>
  );
}

export function BillingPage() {
  const { hasKey } = useSettings();
  const toast = useToast();
  const { t, lang } = useI18n();
  const createOrder = useCreateOrder();

  const [channel, setChannel] = useState<Channel>("usdc");
  const [activeNo, setActiveNo] = useState<string | null>(null);
  const [cryptoOrder, setCryptoOrder] = useState<CreateOrderResponse | null>(null);
  const [payUrl, setPayUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Shared order-status query (deduped with CryptoPayCard's by query key).
  const order = useOrderStatus(activeNo);

  // Single source of the settlement toast for ALL rails.
  const notified = useRef<string | null>(null);
  useEffect(() => {
    const data = order.data;
    if (!data) return;
    if (notified.current === data.out_trade_no) return;
    if (data.status === "paid") {
      toast.success(t("Payment received — credits added"));
      notified.current = data.out_trade_no;
    } else if (data.status === "failed" || data.status === "closed") {
      toast.error(t("Order closed without payment"));
      notified.current = data.out_trade_no;
    }
  }, [order.data, toast, t]);

  function switchChannel(next: Channel) {
    if (next === channel) return;
    setChannel(next);
    setActiveNo(null);
    setCryptoOrder(null);
    setError(null);
    setPayUrl(null);
  }

  async function buy(pkg: PackageId) {
    setError(null);
    setPayUrl(null);
    if (!hasKey) {
      setError(t("Sign in first to purchase credits."));
      return;
    }
    try {
      const res = await createOrder.mutateAsync({ pkg, provider: channel });
      notified.current = null;
      setActiveNo(res.out_trade_no);

      if (channel === "stripe") {
        setCryptoOrder(null);
        if (res.pay_url) {
          setPayUrl(res.pay_url);
        } else {
          toast.error(t("No payment link returned — check the order in Account later"));
        }
      } else {
        if (res.pay_to && res.amount) {
          setCryptoOrder(res);
        } else {
          setError(t("No payment address returned — please retry"));
        }
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(
          e.status === 503
            ? t("This payment channel is not available yet — try another or retry later")
            : t(e.message),
        );
      } else {
        setError(t("Order failed, please retry"));
      }
    }
  }

  const pendingPkg = createOrder.isPending
    ? (createOrder.variables as CreateOrderInput | undefined)?.pkg
    : undefined;

  const buyLabel = channel === "stripe" ? t("Pay with Stripe") : t("Pay with stablecoin");

  return (
    <>
      {payUrl && <GlassCard title={t("Complete your payment")}>
        <a className="copy-btn" href={payUrl} target="_blank" rel="noopener noreferrer">{t("Continue payment")}</a>
        <p>{t("Your payment link is also available in Account → Recent orders.")}</p>
      </GlassCard>}
      <div className="page__head">
        <h1 className="page__title">{t("Buy Credits")}</h1>
        <p className="page__sub">
          {t(
            "1 word = 1 credit — each humanize costs exactly the words you submit. Pay with stablecoins (USDC/USDT) or via Stripe; credits land automatically after payment.",
          )}
        </p>
      </div>

      <div className="seg" role="tablist" aria-label={t("Payment method")}>
        <button
          role="tab"
          aria-selected={channel === "usdc"}
          className={`seg__btn ${channel === "usdc" ? "seg__btn--active" : ""}`}
          onClick={() => switchChannel("usdc")}
        >
          <UsdcMark />
          {"USDC/USDT"}
        </button>
        <button
          role="tab"
          aria-selected={channel === "stripe"}
          className={`seg__btn ${channel === "stripe" ? "seg__btn--active" : ""}`}
          onClick={() => switchChannel("stripe")}
        >
          <StripeMark />
          {t("Stripe (Card / Alipay / WeChat Pay)")}
        </button>
      </div>

      <div className="bill-grid">
        {CATALOG.map((p) => (
          <div key={p.id} className={`pkg ${p.highlight ? "pkg--featured" : ""}`}>
            {p.highlight && <span className="pkg__tag">{t("Recommended")}</span>}
            <div className="pkg__name">{t(p.name)}</div>
            <div className="pkg__price">
              <span>$</span>
              <b>{p.usd}</b>
            </div>
            <div className="pkg__tokens">{wordsLabel(p.words, lang)}</div>
            <div className="pkg__blurb">{t(p.blurb)}</div>
            <Button
              onClick={() => buy(p.id)}
              loading={pendingPkg === p.id}
              disabled={createOrder.isPending}
              block
            >
              {buyLabel}
            </Button>
          </div>
        ))}
      </div>

      {error && <div className="bill-error">{error}</div>}

      {/* USDC: on-chain pay card */}
      {channel === "usdc" && cryptoOrder && (
        <div style={{ marginTop: "var(--s4)" }}>
          <CryptoPayCard
            order={cryptoOrder}
            onPaid={() => order.refetch()}
            onClose={() => {
              setCryptoOrder(null);
              setActiveNo(null);
            }}
          />
        </div>
      )}

      {/* Stripe: order timeline while we poll for the webhook settlement */}
      {channel === "stripe" && activeNo && (
        <div style={{ marginTop: "var(--s4)" }}>
          <GlassCard title={t("Current order")}>
            <div className="order-head">
              <span className="order-no">
                {t("Order #")}
                {activeNo}
              </span>
              {order.data ? (
                <StatusPill tone={orderTone(order.data.status)} dot>
                  {t(orderLabelKey(order.data.status))}
                </StatusPill>
              ) : (
                <StatusPill tone="pending" dot>
                  {t("Checking…")}
                </StatusPill>
              )}
            </div>
            {order.data && <OrderTimeline status={order.data.status} />}
            {order.data?.status === "created" && (
              <p className="page__sub" style={{ marginTop: "var(--s3)" }}>
                {t("Waiting for the payment result — this page polls every 2.5 s.")}
              </p>
            )}
          </GlassCard>
        </div>
      )}
    </>
  );
}
